/**
 * ReviewLightbox — fullscreen image viewer for review photos. Shared by the
 * inline ReviewsSection and the ReviewsModal. Prev/next + keyboard (Esc / ← / →),
 * portaled to document.body so it stacks above the modal.
 */
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import styles from './reviewLightbox.module.css';

export default function ReviewLightbox({ photos, index, onClose, onIndex }) {
  // What a screen reader says is in the shopper's language too (run 4, round B: S8 — "Close" read in English).
  const { t } = useTranslation();
  const goNext = () => onIndex((index + 1) % photos.length);
  const goPrev = () => onIndex((index - 1 + photos.length) % photos.length);
  // Touch swipe — declared before any early return so hook order stays stable.
  const touchX = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onIndex((index + 1) % photos.length);
      else if (e.key === 'ArrowLeft') onIndex((index - 1 + photos.length) % photos.length);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [index, photos.length, onClose, onIndex]);

  if (!photos || photos.length === 0) return null;
  const multi = photos.length > 1;
  const stop = (e) => e.stopPropagation();

  // Touch swipe — left = next, right = previous (mobile-friendly).
  const onTouchStart = (e) => { touchX.current = e.touches[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    if (touchX.current == null || !multi) { touchX.current = null; return; }
    const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
    if (dx < -40) goNext();
    else if (dx > 40) goPrev();
    touchX.current = null;
  };

  return createPortal(
    <div
      className={styles.overlay}
      onClick={(e) => { stop(e); onClose(); }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="dialog"
      aria-modal="true"
      data-testid="review-lightbox"
    >
      <button type="button" className={styles.close} onClick={(e) => { stop(e); onClose(); }} aria-label={t('common.close', { defaultValue: 'Close' })}>
        <FiX />
      </button>
      {multi && (
        <button
          type="button"
          className={`${styles.nav} ${styles.prev}`}
          onClick={(e) => { stop(e); goPrev(); }}
          aria-label={t('productDetail.previousPhoto', { defaultValue: 'Previous photo' })}
          data-testid="review-lightbox-prev"
        >
          <FiChevronLeft />
        </button>
      )}
      <img src={photos[index]} alt="" className={styles.image} onClick={stop} />
      {multi && (
        <button
          type="button"
          className={`${styles.nav} ${styles.next}`}
          onClick={(e) => { stop(e); goNext(); }}
          aria-label={t('productDetail.nextPhoto', { defaultValue: 'Next photo' })}
          data-testid="review-lightbox-next"
        >
          <FiChevronRight />
        </button>
      )}
      {multi && <span className={styles.counter} data-testid="review-lightbox-counter">{index + 1} / {photos.length}</span>}
    </div>,
    document.body,
  );
}
